import React, { createContext, useContext, useState, ReactNode } from 'react'
import CustomAlert from 'src/components/MUI/Alert/Alert'

/**
 * Definición del estado de una alerta individual.
 * 
 * @interface
 * @property {number} id - ID único para identificar cada alerta.
 * @property {boolean} open - Indica si la alerta está visible.
 * @property {'error' | 'warning' | 'info' | 'success'} severity - Nivel de severidad de la alerta.
 * @property {string} message - Mensaje que se muestra en la alerta.
 */
interface AlertState {
  id: number
  open: boolean
  severity: 'error' | 'warning' | 'info' | 'success'
  message: string
}

/**
 * Definición del tipo del contexto del alerta.
 * 
 * @interface
 * @property {function} showAlert - Función para mostrar la alerta con severidad y mensaje.
 * @property {function} hideAlert - Función para ocultar la alerta por su ID.
 */
interface AlertContextType {
  showAlert: (severity: AlertState['severity'], message: string) => void
  hideAlert: (id: number) => void
}

/**
 * Contexto para manejar las alertas.
 * 
 * @type {React.Context<AlertContextType | undefined>}
 */
const AlertContext = createContext<AlertContextType | undefined>(undefined)

/**
 * Hook personalizado para acceder al contexto del alerta.
 * 
 * @throws {Error} Lanza un error si el hook se usa fuera de un `AlertProvider`.
 * @returns {AlertContextType} El contexto del alerta.
 */
export const useAlert = (): AlertContextType => {
  const context = useContext(AlertContext)
  if (context === undefined) {
    throw new Error('useAlert must be used within an AlertProvider')
  }
  return context
}

/**
 * Propiedades del componente `AlertProvider`.
 * 
 * @interface
 * @property {ReactNode} children - Elementos hijos que serán envueltos por el proveedor del contexto.
 */
interface AlertProviderProps {
  children: ReactNode
}

/**
 * Proveedor del contexto de alerta. Envuelve la aplicación o componentes hijos y proporciona las funciones `showAlert` y `hideAlert` para controlar la visibilidad de múltiples alertas.
 * 
 * @component
 * @param {AlertProviderProps} props - Propiedades del proveedor.
 * @returns {JSX.Element} El proveedor del contexto con los componentes hijos.
 * 
 * @example
 * ```tsx

 * 
 * const App = () => {
 *   const { showAlert } = useAlert();
 * 
 *   return (
 *     <div>
 *       <button onClick={() => showAlert('success', 'Operación exitosa')}>Mostrar Alerta</button>
 *     </div>
 *   );
 * }
 * 
 * const Root = () => (
 *   <AlertProvider>
 *     <App />
 *   </AlertProvider>
 * );
 * ```
 */
export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [alerts, setAlerts] = useState<AlertState[]>([])

  /**
   * Muestra una nueva alerta con un mensaje y nivel de severidad.
   * 
   * @param {'error' | 'warning' | 'info' | 'success'} severity - Nivel de severidad de la alerta.
   * @param {string} message - Mensaje que se mostrará en la alerta.
   */
  const showAlert = (severity: AlertState['severity'], message: string) => {
    const newAlert: AlertState = {
      id: Date.now(), // Utiliza la fecha actual como ID único
      open: true,
      severity,
      message
    }
    setAlerts((prevAlerts) => [...prevAlerts, newAlert])
  }

  /**
   * Oculta una alerta por su ID.
   * 
   * @param {number} id - El ID de la alerta a ocultar.
   */
  const hideAlert = (id: number) => {
    setAlerts((prevAlerts) => prevAlerts.filter((alert) => alert.id !== id))
  }

  return (
    <AlertContext.Provider value={{ showAlert, hideAlert }}>
      {alerts.map((alert) => (
        <CustomAlert
          key={alert.id}
          onRemove={hideAlert}
          alerts={[{ id: alert.id, severity: alert.severity, message: alert.message }]}
        />
      ))}
      {children}
    </AlertContext.Provider>
  )
}

